
import { Component, Mixins } from 'vue-property-decorator';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import GlobalStore from '@/store/modules/global';
import { getModule } from 'vuex-module-decorators';
import { MachineSWUpdateData } from '@/models/MachineSWUpdateData';
import MenuStore from '@/store/modules/menu';
import SingleMachineMap from '@/components/Map/SingleMachineMap.vue';
import MqttMixIn from '@/mixins/mqttManagement';
import UpdateMixIn from '@/mixins/softwareUpdate';
import { PubSub } from '@/PubSubClass';
import UpdateStore from '@/store/modules/update';
import router from '@/router';
import { MachineSoftwareUpdateState } from '@/models/MachineSoftwareUpdateState';
import { UserProceedWithUpdateResponse } from '@/models/UserProceedWithUpdateResponse';

// Define the component in class-style
/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({ name: 'SoftwareUpdate', inject: ['container'], components: { SingleMachineMap } })
export default class UploadPath extends Mixins(MqttMixIn, UpdateMixIn) {
  globalStore = getModule(GlobalStore, this.$store);
  public menuStore = getModule(MenuStore, this.$store);
  updateStore = getModule(UpdateStore, this.$store);
  status: MachineSWUpdateData | null = this.updateStore.currentMachineUpdate;
  remoteTimerInterval = 0;
  remoteTimer = 20; // 1 second increments
  publishRepeaterInterval = 0;
  windowSize = {
    x: 0,
    y: 0
  };
  mapHeightBottomStyle = 'calc(100% - 80px)';
  mapHeightFullStyle = 'calc(100%)';
  // IoC container provided from App.ts is injected here
  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  private container!: Container;

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
    this.addMqttVisibility(this.globalStore.softwareUpdateTopic, () =>
      PubSub.subscribe(this.globalStore.softwareUpdateTopic).subscribe({
        next: data => this.updateRecieved(data),
        error: error => this.mqttError(error)
      })
    );
  }

  async mounted() {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();
    this.onResize();
    if (this.status == null) this.returnToHome();
    this.actions.forEach((act, key) => {
      this.subscriptions.set(key, act());
    });

    this.publishRepeaterInterval = window.setInterval(this.publishRepeaterTimer, 1000);
  }

  destroyed() {
    this.removeMqttVisibility();
    clearInterval(this.remoteTimerInterval);
    clearInterval(this.publishRepeaterInterval);
  }

  get timerPercent() {
    return 100 - (this.remoteTimer / 20) * 100;
  }
  get progressValue() {
    if (this.status == null) return 0;

    const progress = (this.status.update_steps_completed / this.status.total_update_steps) * 100;
    return Math.round(progress);
  }

  get progressMessage() {
    if (this.status == null) return this.$t('views.software_update.states.unknown').toString();
    const currentStep = this.status.update_steps_completed;
    const totalSteps = this.status.total_update_steps;
    return `${this.$t('views.software_update.step').toString()} ${currentStep} / ${totalSteps}`;
  }
  containerWidth() {
    return this.menuStore.drawer ? this.windowSize.x - 380 : this.windowSize.x - 140;
  }
  getMapHeight() {
    if (this.status == null) return this.mapHeightFullStyle;
    return this.status?.software_update_state >= MachineSoftwareUpdateState.WAITING_FOR_UPDATE_TO_COMPLETE
      ? this.mapHeightBottomStyle
      : this.mapHeightFullStyle;
  }
  onResize() {
    this.windowSize = { x: window.innerWidth, y: window.innerHeight };
  }
  async countDownRemoteTimer() {
    this.remoteTimer -= 1;
    if (this.status == null) return;
    if (this.remoteTimer <= 0) {
      clearInterval(this.remoteTimerInterval);
      this.cancelEStop();
    } else if (this.status?.software_update_state >= MachineSoftwareUpdateState.WAITING_FOR_UPDATE_TO_COMPLETE) {
      clearInterval(this.remoteTimerInterval);
      this.remoteTimer = 20;
    }
  }

  async publishRepeaterTimer() {
    const uiPayload = this.lastUIUpdate;
    if (uiPayload == null || this.status == null) return;
    if (this.status.software_update_state >= MachineSoftwareUpdateState.UPDATE_COMPLETED_SUCCESS) return;
    this.publishUIPayload(uiPayload);
  }
  async cancelEStop() {
    if (this.status == null) return;
    try {
      const uiPayload = this.createUIUpdatePayload(UserProceedWithUpdateResponse.UNKNOWN, this.status);
      this.lastUIUpdate = uiPayload;
      this.publishUIPayload(uiPayload);
      await this.returnToHome();
    } catch (err) {
      console.log(err);
    }
  }

  async returnToHome() {
    try {
      this.updateStore.changeCurrentMachine(null);
    } catch (error) {
      console.log(error);
    } finally {
      router.back();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private updateRecieved(data: any): void {
    const response = data.value as string;
    const json = JSON.stringify(response);
    console.info('Update Status Event', json);
    const status = MachineSWUpdateData.toModel(json);
    if (status.machine_id != this.updateStore.currentMachineUpdate?.machine_id) return;
    this.status = status;

    switch (this.status.software_update_state) {
      case MachineSoftwareUpdateState.WAITING_FOR_ACTIVE_ESTOP:
        if (this.remoteTimerInterval == 0) {
          this.remoteTimer = 20;
          this.remoteTimerInterval = window.setInterval(this.countDownRemoteTimer, 1000);
        }
        break;

      default:
        clearInterval(this.remoteTimerInterval);
        this.remoteTimerInterval = 0;
        break;
    }
  }

  get updateStatusDisplay(): string {
    if (this.status == null) return '';

    switch (this.status.software_update_state) {
      case MachineSoftwareUpdateState.UNKNOWN:
        return this.$t('views.software_update.states.unknown').toString();
      case MachineSoftwareUpdateState.WAITING_FOR_USER_CONFIRMATION:
        return this.$t('views.software_update.states.user_confirm').toString();
      case MachineSoftwareUpdateState.WAITING_FOR_UPDATE_TO_COMPLETE:
        return this.$t('views.software_update.states.update_in_progress').toString();
      case MachineSoftwareUpdateState.WAITING_FOR_ACTIVE_ESTOP:
        return this.$t('views.software_update.states.estop').toString();
      case MachineSoftwareUpdateState.UPDATE_COMPLETED_SUCCESS:
        return this.$t('views.software_update.states.complete').toString();
      case MachineSoftwareUpdateState.UPDATE_COMPLETED_FAIL:
        return this.$t('views.software_update.states.failed').toString();
      case MachineSoftwareUpdateState.CHECKING_FOR_UPDATES:
        return this.$t('views.software_update.states.checking_for_updates').toString();
      default:
        return this.$t('views.software_update.states.unknown').toString();
    }
  }
}
